import { useEffect } from "react";

import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useField } from "formik";

import { Translate } from "@/components/Application/Translate";

interface Props {
	label: string;
	name: string;
	id: string;
	options: { value: string; label: string }[];
	translationString?: string;
	style?: React.CSSProperties;
	disabled?: boolean;
}

const CustomSelectCheckbox = ({
	label,
	id,
	options,
	translationString,
	...props
}: Props) => {
	const [field, meta, helpers] = useField(props.name);

	// Ensure selected values are stored as an array of strings
	const selectedValues = field.value ? field.value.map(String) : [];

	useEffect(() => {
		if (props.disabled) {
			helpers.setValue([]);
		}
	}, [props.disabled]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (selectedValues.includes(value)) {
			helpers.setValue(selectedValues.filter((v) => v !== value)); // Unselect
		} else {
			helpers.setValue([...selectedValues, value]); // Select
		}
	};

	const hasError = meta.touched && meta.error;

	return (
		<FormControl
			component="fieldset"
			error={hasError}
			style={{ width: "100%", ...props.style }}
		>
			<FormLabel
				component="legend"
				htmlFor={id}
				style={{ marginBottom: "8px" }}
			>
				<Translate String={translationString || ""}>{label}</Translate>
			</FormLabel>
			<Box
				sx={{
					maxHeight: "460px",
					overflowY: "auto",
					padding: "8px 16px",
					border: "1px solid #ccc",
					borderRadius: "4px",
				}}
			>
				<FormGroup>
					{options.map((option) => (
						<FormControlLabel
							key={option.value}
							control={
								<Checkbox
									checked={
										selectedValues.includes(option.value) && !props.disabled
									}
									onChange={handleChange}
									value={option.value}
									color="primary"
									disabled={props.disabled}
								/>
							}
							label={option.label}
							style={{ marginBottom: "8px" }}
						/>
					))}
				</FormGroup>
			</Box>
			{hasError && <FormHelperText>{meta.error}</FormHelperText>}
		</FormControl>
	);
};

export default CustomSelectCheckbox;
